<script setup lang="ts">
import { computed, ref, watch, type Component } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { AuthorizedLayout, ProfileDropdown } from '@ui'
import type { AppHeaderItem } from '@ui'
import type { MenuItem } from '@ui/profile-dropdown/type'
import {
  IconBuildingHospital,
  IconBulb,
  IconChartBar,
  IconChevronDown,
  IconEye,
  IconInbox,
  IconLanguage,
  IconLayoutDashboard,
  IconLogout,
  IconMoon,
  IconPencil,
  IconReportAnalytics,
  IconSettings,
  IconSparkles,
  IconSun,
  IconSwitchHorizontal,
  IconTarget,
  IconUsers,
} from '@tabler/icons-vue'
import RailLayout from '@/ui-delta/RailLayout.vue'
import SideNav, { type SideNavItem } from '@/ui-delta/SideNav.vue'
import ThemeToggle from '@/ui-delta/ThemeToggle.vue'
import MenuBurger from '@/ui-delta/MenuBurger.vue'
import { useTheme } from '@/ui-delta/useTheme'
import ElfieLockup from '@/components/core/ElfieLockup.vue'
import UserFormModal, { type UserFormDraft } from '@/components/users/UserFormModal.vue'
import UserAvatar from '@/components/users/UserAvatar.vue'
import ViewAsModal from '@/components/users/ViewAsModal.vue'
import DemonstrationMarker from '@/components/workspaces/DemonstrationMarker.vue'
import IntelligenceHeader from '@/components/workspaces/IntelligenceHeader.vue'
import AnalystDock from '@/components/analyst/AnalystDock.vue'
import { useAskElfieStore } from '@/stores/ask-elfie'
import { useMyWorkspaces, useWorkspace } from '@/api/workspaces'
import { screenContextOf } from '@/lib/analyst'
import {
  analysisPages,
  landingPage,
  pageLabel,
  settingsTabs,
  switchTarget,
  type AnalysisPage,
} from '@/lib/intelligence-pages'
import { useAuthStore } from '@/stores/auth'
import {
  useLogout,
  useRemoveAvatar,
  useSetAvatar,
  useStartViewAs,
  useStopViewAs,
  useUpdateProfile,
} from '@/api/auth'
import { isApiError } from '@/api/client'
import { useNotificationStore } from '@ui'
import { buildInfo, formatBuildStamp } from '@/lib/build-info'
import { applyLocale, currentLocale, readStoredLocale } from '@/i18n/locale-loader'
import {
  DEFAULT_LOCALE,
  LOCALE_LABELS,
  SUPPORTED_LOCALES,
  isSupportedLocale,
  type SupportedLocale,
} from '@/i18n/locales'

const { t, locale: activeLocale } = useI18n()
const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const { user, isAdmin } = storeToRefs(auth)
const logout = useLogout()
const updateProfile = useUpdateProfile()
const setAvatar = useSetAvatar()
const removeAvatar = useRemoveAvatar()
const { resolved: resolvedTheme, toggle: toggleTheme } = useTheme()
const startViewAs = useStartViewAs()
const stopViewAs = useStopViewAs()
const notifications = useNotificationStore()

/*
 * The product areas, one tab each in the app bar. Add a tab here, its home in
 * HEADER_HOMES, its rail below and a section in the phone menu when a new
 * product area lands. Inside a workspace's Intelligence there are none: the
 * dedicated shell has one navigation axis, its own (P1 §4).
 */
const headerItems = computed<AppHeaderItem[]>(() =>
  inWorkspace.value ? [] : [{ name: 'home', label: t('nav.home') }],
)

/**
 * A rail item and the paths it lights up on. `to` is where it goes; `owns`
 * names the pages reached from it that live under a different prefix, so the
 * item stays lit on them instead of handing the highlight to the section home.
 */
type RailItem = SideNavItem & { to?: string; owns?: string[] }

/** The product's own sitemap. Every item carries an icon: the rail collapses to icons. */
const railItems = computed<RailItem[]>(() => [
  { name: 'home', label: t('nav.home'), icon: IconLayoutDashboard, to: '/' },
])

/*
 * ─── Settings ───────────────────────────────────────────────────────────────
 * Administration is not part of the product's sitemap: it is reached from the
 * profile menu (admin only) and gets a rail of its own while you are in it.
 */
const inSettings = computed(() => route.path.startsWith('/admin'))

const settingsRailItems = computed<RailItem[]>(() => [
  { name: 'users', label: t('nav.userManagement'), icon: IconUsers, to: '/admin/users' },
  {
    name: 'workspaces',
    label: t('nav.workspaceManagement'),
    icon: IconBuildingHospital,
    to: '/admin/workspaces',
  },
  {
    name: 'languages',
    label: t('nav.languageManagement'),
    icon: IconLanguage,
    to: '/admin/languages',
  },
])

/*
 * ─── A workspace's Intelligence ─────────────────────────────────────────────
 * `/workspaces/:workspaceId/intelligence/…` gets the dedicated shell (P1 §4):
 * no product tabs; the workspace and `Switch to Operations` on the right of
 * the header (IntelligenceHeader); and a DEMONSTRATION workspace's marker
 * beside the product's name (S0 §55.3). Its left menu is for the analysis
 * pages (Overview, Opportunities…), with the gear to Intelligence settings
 * kept apart at its foot (DECISIONS.md #17, #43); it is drawn once there is
 * either. The router lets in only someone the API would; the shell only draws
 * what the API says.
 */
const workspaceId = computed(() =>
  route.meta.layout === 'intelligence' && typeof route.params.workspaceId === 'string'
    ? route.params.workspaceId
    : undefined,
)
const workspace = useWorkspace(workspaceId)
// Refused after all (access changed while the page was open): the page says why, and Home is the way out.
const inWorkspace = computed(() => workspaceId.value !== undefined && !workspace.isError.value)
const isDemonstration = computed(
  () => inWorkspace.value && workspace.data.value?.dataProvenanceMode === 'DEMONSTRATION',
)
/** What the page on screen shows, for Ask Elfie to inherit (P4 §3); null where it is not offered. */
const analystContext = computed(() => (inWorkspace.value ? screenContextOf(route) : null))
/** P4: Ask Elfie on analysis pages, for whoever may use Intelligence here; the API refuses anyone else. */
const askElfieOffered = computed(() => inWorkspace.value && !!workspace.data.value?.can.intelligence && analystContext.value !== null)
const askElfie = useAskElfieStore()
/** The side menu's way into Ask Elfie: it opens the panel rather than a page (P4 §C7.1). */
const ASK_ELFIE = 'ask-elfie'
/** For the workspace switcher: the same list Home shows. */
const myWorkspaces = useMyWorkspaces(() => inWorkspace.value && !user.value?.impersonatedBy)

/** Each page of the left menu carries an icon: the rail collapses to icons. */
const ANALYSIS_ICONS: Record<AnalysisPage, Component> = {
  'workspace-overview': IconChartBar,
  'workspace-opportunities': IconBulb,
  'workspace-objectives': IconTarget,
  'workspace-reports': IconReportAnalytics,
  'workspace-attention': IconInbox,
}

/** The analysis pages of the left menu this person may open: the API decides (`can`), the rail follows. */
const workspaceRailItems = computed<RailItem[]>(() => {
  const access = workspace.data.value
  if (!access) return []
  const pages: RailItem[] = analysisPages(access).map((name) => ({
    name,
    label: t(pageLabel(name)),
    icon: ANALYSIS_ICONS[name],
    to: router.resolve({ name, params: { workspaceId: access.workspaceId } }).path,
  }))
  return askElfieOffered.value ? [...pages, { name: ASK_ELFIE, label: t('analyst.name'), icon: IconSparkles }] : pages
})

/**
 * Intelligence settings, at the foot of the left menu (DECISIONS.md #43): it
 * opens the first tab this person may see, and is lit on every tab.
 */
const workspaceFootItems = computed<RailItem[]>(() => {
  const access = workspace.data.value
  if (!access) return []
  const tabs = settingsTabs(access).map((name) => router.resolve({ name, params: { workspaceId: access.workspaceId } }).path)
  const [first, ...others] = tabs
  return first ? [{ name: 'intelligence-settings', label: t('nav.intelligenceSettings'), icon: IconSettings, to: first, owns: others }] : []
})

const activeRailItems = computed(() => {
  if (inSettings.value) return settingsRailItems.value
  return inWorkspace.value ? workspaceRailItems.value : railItems.value
})
const activeFootItems = computed(() => (!inSettings.value && inWorkspace.value ? workspaceFootItems.value : []))
const allRailItems = computed(() => [...activeRailItems.value, ...activeFootItems.value])

const railHeading = computed(() => {
  if (inSettings.value) return t('nav.settings')
  if (inWorkspace.value) return workspace.data.value?.name ?? t('common.loading')
  return t('nav.home')
})

/** The longest of the item's paths the current route starts with; 0 if none. */
const matchLength = (item: RailItem): number =>
  Math.max(
    0,
    ...[item.to, ...(item.owns ?? [])]
      .filter(
        (path): path is string =>
          !!path && (path === '/' ? route.path === '/' : route.path.startsWith(path)),
      )
      .map((path) => path.length),
  )

// Longest match, so a nested page does not also light up its parent.
const activeRail = computed(
  () =>
    allRailItems.value
      .map((item) => ({ item, length: matchLength(item) }))
      .filter(({ length }) => length > 0)
      .sort((a, b) => b.length - a.length)[0]?.item.name,
)

const goTo = (name: string): void => {
  if (name === ASK_ELFIE) return askElfie.show()
  const target = allRailItems.value.find((item) => item.name === name)
  if (target?.to) void router.push(target.to)
}

/*
 * ─── Phone navigation ───────────────────────────────────────────────────────
 * Below 1024px the shared header switches to its compact form: hamburger,
 * centred section title, avatar. The header tabs disappear with it, so the
 * menu behind the hamburger carries every section and its pages — the same
 * lists the rails use, grouped by section.
 */
interface PhonePage {
  name: string
  label: string
  to: string
  owns?: string[]
}

const pagesOf = (items: RailItem[]): PhonePage[] =>
  items.flatMap((item) =>
    'to' in item && item.to && 'name' in item && item.name && item.label
      ? [{ name: item.name, label: item.label, to: item.to, owns: item.owns }]
      : [],
  )

/** How much of the current route the page's paths cover: 0 if none, else the longest. */
const phoneMatchLength = (page: PhonePage): number =>
  Math.max(
    0,
    ...[page.to, ...(page.owns ?? [])]
      .filter((path) =>
        path === '/' ? route.path === '/' : route.path === path || route.path.startsWith(`${path}/`),
      )
      .map((path) => path.length),
  )

/**
 * The page of a list the current route is on. Longest match, as the rail does,
 * so Members & seats (`…/settings`) is not lit on the record (`…/settings/transparency`).
 */
const activePageOf = (pages: PhonePage[]): string | undefined =>
  pages
    .map((page) => ({ page, length: phoneMatchLength(page) }))
    .filter(({ length }) => length > 0)
    .sort((a, b) => b.length - a.length)[0]?.page.name

const homePages = computed(() => pagesOf(railItems.value))
const adminPages = computed(() => pagesOf(settingsRailItems.value))

/** On a phone the left menu is the hamburger's panel: it lists the settings tabs this person may see, after the pages. */
const workspaceSettingsPages = computed<PhonePage[]>(() => {
  const access = workspace.data.value
  if (!access) return []
  return settingsTabs(access).map((name) => ({
    name,
    label: t(pageLabel(name)),
    to: router.resolve({ name, params: { workspaceId: access.workspaceId } }).path,
  }))
})

const workspacePages = computed(() => [...pagesOf(workspaceRailItems.value), ...workspaceSettingsPages.value])

/**
 * The phone's workspace switcher: the other workspaces there is a page to
 * open in, each on the same page when it is theirs there, as the header's.
 */
const switchPages = computed<PhonePage[]>(() =>
  (myWorkspaces.data.value ?? []).flatMap((other) => {
    if (other.workspaceId === workspaceId.value || landingPage(other) === null) return []
    const page = switchTarget(other, route.name)
    return page
      ? [
          {
            name: `switch-to-${other.workspaceId}`,
            label: other.name,
            to: router.resolve({ name: page, params: { workspaceId: other.workspaceId } }).path,
          },
        ]
      : []
  }),
)

/** Which section the current route belongs to. */
const currentSection = computed(() => {
  if (inSettings.value) return 'settings'
  return inWorkspace.value ? 'workspace' : 'home'
})

/*
 * One section open at a time, the current one by default. Re-seeded every
 * time the panel opens.
 */
const openSection = ref<string>(currentSection.value)
const languageOpen = ref(false)
watch(currentSection, (section) => (openSection.value = section))

/** Every opening starts from the section you are in, whatever was left open last time. */
const onMenuToggle = (wasOpen: boolean): void => {
  if (!wasOpen) {
    openSection.value = currentSection.value
    languageOpen.value = false
  }
}

const toggleSection = (name: string): void => {
  openSection.value = openSection.value === name ? '' : name
  languageOpen.value = false
}

const openFromMenu = (to: string, close: () => void): void => {
  close()
  void router.push(to)
}

const fromMenu = (close: () => void, action: () => void): void => {
  close()
  action()
}

const roleLabel = computed(() => (user.value ? t(`users.roles.${user.value.role}`) : ''))

const logOut = async (): Promise<void> => {
  await logout.mutateAsync().catch(() => undefined)
  auth.clear()
  await router.replace({ name: 'login' })
}

const HEADER_HOMES: Record<string, string> = {
  home: '/',
}

function onHeaderNavigate(item: AppHeaderItem): void {
  void router.push(HEADER_HOMES[item.name] ?? '/')
}

/*
 * ─── Profile: edit myself ───────────────────────────────────────────────────
 * Clicking the name in the menu opens the same form the admin uses on User
 * management, in `self` mode.
 */
const profileOpen = ref(false)

const profileDraft = computed<Partial<UserFormDraft>>(() => ({
  name: user.value?.name ?? '',
  preferredName: user.value?.preferredName ?? '',
  email: user.value?.email ?? '',
  function: user.value?.function ?? '',
  role: user.value?.role ?? 'user',
}))

const failWith = (error: unknown): void => {
  notifications.showNotification({
    status: 'DANGER',
    text: isApiError(error) ? error.message : t('common.somethingWentWrong'),
  })
}

const saveProfile = async (draft: UserFormDraft): Promise<void> => {
  try {
    const saved = await updateProfile.mutateAsync({
      name: draft.name,
      preferredName: draft.preferredName,
      function: draft.function,
    })
    auth.setUser(saved)
    profileOpen.value = false
    notifications.showNotification({ status: 'SUCCESS', text: t('profile.saved') })
  } catch (error) {
    failWith(error)
  }
}

// A picture is applied as soon as it is chosen, like the language, not on Save.
const changePicture = async (file: File | null): Promise<void> => {
  try {
    auth.setUser(file ? await setAvatar.mutateAsync(file) : await removeAvatar.mutateAsync())
  } catch (error) {
    failWith(error)
  }
}

/*
 * ─── Language ───────────────────────────────────────────────────────────────
 * The saved preference wins; the browser's remembered choice covers a user who
 * has not saved one yet. Applied whenever the signed-in user changes, so a
 * fresh login lands in the right language without a reload.
 */
watch(
  () => user.value?.locale,
  (saved) => {
    const next = isSupportedLocale(saved) ? saved : (readStoredLocale() ?? DEFAULT_LOCALE)
    if (next !== currentLocale()) void applyLocale(next).catch(failWith)
  },
  { immediate: true },
)

const chooseLocale = async (next: SupportedLocale): Promise<void> => {
  if (next === currentLocale()) return
  try {
    await applyLocale(next)
    const saved = await updateProfile.mutateAsync({ locale: next })
    auth.setUser(saved)
  } catch (error) {
    failWith(error)
  }
}

/*
 * ─── View as ────────────────────────────────────────────────────────────────
 * Offered only to an admin who is themselves. While viewing, the menu shows
 * the viewed user and the banner above the rail carries the exit.
 */
const viewAsOpen = ref(false)
const viewing = computed(() => user.value?.impersonatedBy ?? null)

const beginViewAs = async (userId: string): Promise<void> => {
  try {
    await startViewAs.mutateAsync(userId)
  } catch (error) {
    failWith(error)
  }
}

const exitViewAs = async (): Promise<void> => {
  try {
    await stopViewAs.mutateAsync()
  } catch (error) {
    failWith(error)
  }
}

const profileMenuItems = computed<MenuItem[]>(() => [
  ...(isAdmin.value && !viewing.value
    ? [
        {
          label: t('nav.settings'),
          icon: IconSettings,
          onClick: (): void => void router.push('/admin/users'),
        },
        {
          label: t('viewAs.menu'),
          icon: IconEye,
          onClick: (): void => {
            viewAsOpen.value = true
          },
        },
      ]
    : []),
  {
    label: t('profile.language'),
    icon: IconLanguage,
    onClick: (): void => undefined,
    children: SUPPORTED_LOCALES.map((code) => ({
      label: LOCALE_LABELS[code],
      isActive: activeLocale.value === code,
      onClick: (): void => void chooseLocale(code),
    })),
  },
  {
    label: t('nav.logOut'),
    variant: 'danger' as const,
    divider: true,
    onClick: (): void => void logOut(),
  },
])

/*
 * ─── Build stamp ────────────────────────────────────────────────────────────
 * One muted line at the foot of the menu; click copies it for a bug report.
 */
const buildStamp = computed(() => formatBuildStamp(buildInfo, activeLocale.value))
const stampCopied = ref(false)

const copyBuildStamp = async (): Promise<void> => {
  try {
    await navigator.clipboard.writeText(buildStamp.value)
    stampCopied.value = true
    setTimeout(() => (stampCopied.value = false), 1500)
  } catch {
    // No clipboard permission — the text is on screen to be selected instead.
  }
}
</script>

<template>
  <AuthorizedLayout
    :items="headerItems"
    :max-visible-items="headerItems.length"
    :active-item="inSettings || inWorkspace ? '' : 'home'"
    mobile-show-menu-button
    mobile-compact-header
    :mobile-title="railHeading"
    :menu-open-title="t('nav.menu')"
    :menu-icon="MenuBurger"
    :menu-open-icon="MenuBurger"
    @navigate="onHeaderNavigate"
    @menu-button-click="onMenuToggle"
  >
    <!-- The panel behind the hamburger: every section, every page. -->
    <template #mega-menu="{ closeMenu }">
      <nav class="phone-menu" :aria-label="t('nav.menu')" data-test="phone-menu">
        <div class="phone-menu__sections">
          <section class="phone-menu__section" :class="{ 'is-open': openSection === 'home' }">
            <button
              type="button"
              class="phone-menu__section-head"
              :aria-expanded="openSection === 'home'"
              @click="toggleSection('home')"
            >
              <span>{{ t('nav.home') }}</span>
              <IconChevronDown :size="18" class="phone-menu__caret" />
            </button>
            <ul v-if="openSection === 'home'" class="phone-menu__list">
              <li v-for="item in homePages" :key="item.name">
                <button
                  type="button"
                  class="phone-menu__item"
                  :class="{ 'is-active': item.name === activePageOf(homePages) }"
                  @click="openFromMenu(item.to, closeMenu)"
                >
                  {{ item.label }}
                </button>
              </li>
            </ul>
          </section>

          <!--
            The workspace you are in: its pages, its settings, and the way back
            to Operations. The kit's header ignores taps on its right-hand side
            on a phone, so what sits there on a desktop is here (P1 §3.2, §4).
          -->
          <section
            v-if="inWorkspace && workspace.data.value"
            class="phone-menu__section"
            :class="{ 'is-open': openSection === 'workspace' }"
            data-test="phone-menu-workspace"
          >
            <button
              type="button"
              class="phone-menu__section-head"
              :aria-expanded="openSection === 'workspace'"
              @click="toggleSection('workspace')"
            >
              <span>{{ railHeading }}</span>
              <IconChevronDown :size="18" class="phone-menu__caret" />
            </button>
            <ul v-if="openSection === 'workspace'" class="phone-menu__list">
              <li v-for="item in workspacePages" :key="item.name">
                <button
                  type="button"
                  class="phone-menu__item"
                  :class="{ 'is-active': item.name === activePageOf(workspacePages) }"
                  :data-test="`phone-page-${item.name}`"
                  @click="openFromMenu(item.to, closeMenu)"
                >
                  {{ item.label }}
                </button>
              </li>
              <li>
                <!-- Another web product, so a link: it opens where ElfieCare keeps this workspace. -->
                <a
                  v-if="workspace.data.value.operationsUrl"
                  :href="workspace.data.value.operationsUrl"
                  class="phone-menu__item phone-menu__item--icon"
                  data-test="phone-switch-to-operations"
                >
                  <IconSwitchHorizontal :size="18" aria-hidden="true" />
                  {{ t('intelligence.switchToOperations') }}
                </a>
                <template v-else>
                  <span
                    class="phone-menu__item phone-menu__item--icon is-disabled"
                    role="link"
                    aria-disabled="true"
                    data-test="phone-switch-to-operations"
                  >
                    <IconSwitchHorizontal :size="18" aria-hidden="true" />
                    {{ t('intelligence.switchToOperations') }}
                  </span>
                  <p class="phone-menu__note">{{ t('intelligence.switchToOperationsUnset') }}</p>
                </template>
              </li>
            </ul>
          </section>

          <section
            v-if="inWorkspace && switchPages.length > 0"
            class="phone-menu__section"
            :class="{ 'is-open': openSection === 'switch' }"
            data-test="phone-menu-switch"
          >
            <button
              type="button"
              class="phone-menu__section-head"
              :aria-expanded="openSection === 'switch'"
              @click="toggleSection('switch')"
            >
              <span>{{ t('intelligence.workspaceSwitcher') }}</span>
              <IconChevronDown :size="18" class="phone-menu__caret" />
            </button>
            <ul v-if="openSection === 'switch'" class="phone-menu__list">
              <li v-for="item in switchPages" :key="item.name">
                <button
                  type="button"
                  class="phone-menu__item"
                  :data-test="`phone-${item.name}`"
                  @click="openFromMenu(item.to, closeMenu)"
                >
                  {{ item.label }}
                </button>
              </li>
            </ul>
          </section>

          <!-- Settings: the admin pages, then what is mine. -->
          <section class="phone-menu__section" :class="{ 'is-open': openSection === 'settings' }">
            <button
              type="button"
              class="phone-menu__section-head"
              :aria-expanded="openSection === 'settings'"
              @click="toggleSection('settings')"
            >
              <span>{{ t('nav.settings') }}</span>
              <IconChevronDown :size="18" class="phone-menu__caret" />
            </button>
            <ul v-if="openSection === 'settings'" class="phone-menu__list">
              <template v-if="isAdmin && !viewing">
                <li v-for="item in adminPages" :key="item.name">
                  <button
                    type="button"
                    class="phone-menu__item"
                    :class="{ 'is-active': item.name === activePageOf(adminPages) }"
                    @click="openFromMenu(item.to, closeMenu)"
                  >
                    {{ item.label }}
                  </button>
                </li>
              </template>
              <li>
                <button
                  type="button"
                  class="phone-menu__item phone-menu__item--icon"
                  @click="fromMenu(closeMenu, () => (profileOpen = true))"
                >
                  <IconPencil :size="18" />
                  {{ t('profile.editTitle') }}
                </button>
              </li>
              <li>
                <button
                  type="button"
                  class="phone-menu__item phone-menu__item--icon"
                  @click="toggleTheme"
                >
                  <component :is="resolvedTheme === 'dark' ? IconSun : IconMoon" :size="18" />
                  {{
                    resolvedTheme === 'dark'
                      ? t('profile.switchToLight')
                      : t('profile.switchToDark')
                  }}
                </button>
              </li>
              <li v-if="isAdmin && !viewing">
                <button
                  type="button"
                  class="phone-menu__item phone-menu__item--icon"
                  @click="fromMenu(closeMenu, () => (viewAsOpen = true))"
                >
                  <IconEye :size="18" />
                  {{ t('viewAs.menu') }}
                </button>
              </li>
              <li>
                <button
                  type="button"
                  class="phone-menu__item phone-menu__item--icon"
                  :aria-expanded="languageOpen"
                  @click="languageOpen = !languageOpen"
                >
                  <IconLanguage :size="18" />
                  <span class="phone-menu__grow">{{ t('profile.language') }}</span>
                  <span class="phone-menu__value">{{ LOCALE_LABELS[currentLocale()] }}</span>
                </button>
                <div v-if="languageOpen" class="phone-menu__chips">
                  <button
                    v-for="code in SUPPORTED_LOCALES"
                    :key="code"
                    type="button"
                    class="phone-menu__chip"
                    :class="{ 'is-active': activeLocale === code }"
                    @click="chooseLocale(code)"
                  >
                    {{ LOCALE_LABELS[code] }}
                  </button>
                </div>
              </li>
            </ul>
          </section>
        </div>

        <div class="phone-menu__foot">
          <button
            type="button"
            class="phone-menu__item phone-menu__item--icon phone-menu__item--danger"
            @click="fromMenu(closeMenu, () => void logOut())"
          >
            <IconLogout :size="18" />
            {{ t('nav.logOut') }}
          </button>
          <button
            type="button"
            class="phone-menu__build"
            :title="t('profile.copyBuild')"
            @click="copyBuildStamp"
          >
            {{ stampCopied ? t('profile.buildCopied') : buildStamp }}
          </button>
        </div>
      </nav>
    </template>

    <template #logo>
      <RouterLink to="/" class="shell__brand" :aria-label="t('common.appName')">
        <ElfieLockup size="lg" inverted />
      </RouterLink>
      <!-- P1 §4 and S0 §55.3: beside the product's name, on every page of the workspace. -->
      <DemonstrationMarker v-if="isDemonstration" compact class="shell__demo-chip" />
    </template>

    <!--
      The `user` slot's default IS the account dropdown, so overriding it has to
      re-render it — an earlier version replaced the slot with the theme toggle
      alone and removed the account entirely. The toggle sits immediately left
      of the name, as specified.
    -->
    <template #user>
      <div class="shell__account">
        <IntelligenceHeader
          v-if="inWorkspace && workspace.data.value"
          class="shell__intelligence"
          :access="workspace.data.value"
          :workspaces="myWorkspaces.data.value ?? [workspace.data.value]"
        />
        <ThemeToggle on-field class="shell__theme" />
        <!--
          On a phone the library makes this area display-only, taps included,
          so it shows the brand mark; the menu carries the controls.
        -->
        <span class="shell__coin" aria-hidden="true"><ElfieLockup size="md" inverted /></span>
        <ProfileDropdown
          :user-name="user?.name ?? ''"
          :user-email="roleLabel"
          :menu-items="profileMenuItems"
          max-width="300px"
        >
          <!--
            The default trigger shows the name only. The account block carries
            two lines — who you are and in what capacity — so the trigger is
            supplied rather than left to the default.
          -->
          <template #trigger="{ user: account }">
            <button type="button" class="account">
              <UserAvatar
                class="account__avatar"
                :name="user?.name ?? ''"
                :avatar-url="user?.avatarUrl"
              />
              <span class="account__text">
                <span class="account__name">{{ account.name }}</span>
                <span v-if="account.email" class="account__role">{{ account.email }}</span>
              </span>
              <IconChevronDown :size="16" class="account__chevron" />
            </button>
          </template>

          <!-- The name is a button: it opens "edit my profile". -->
          <template #header="{ user: account }">
            <button
              type="button"
              class="profile-head"
              data-test="profile-edit"
              :title="t('profile.editTitle')"
              @click="profileOpen = true"
            >
              <UserAvatar
                class="profile-head__avatar"
                :name="user?.name ?? ''"
                :avatar-url="user?.avatarUrl"
                :size="40"
              />
              <span class="profile-head__text">
                <span class="profile-head__name">{{ account.name }}</span>
                <span v-if="account.email" class="profile-head__role">{{ account.email }}</span>
              </span>
              <IconPencil :size="16" class="profile-head__pencil" />
            </button>
          </template>

          <!--
            The library's footer slot. It is named for a timezone line the
            library's products show there; this product shows the build instead.
          -->
          <template #timezone>
            <button
              type="button"
              class="build-stamp"
              data-test="build-stamp"
              :title="t('profile.copyBuild')"
              @click="copyBuildStamp"
            >
              {{ stampCopied ? t('profile.buildCopied') : buildStamp }}
            </button>
          </template>
        </ProfileDropdown>
      </div>
    </template>

    <!-- Always visible while viewing as someone else: the way back must never scroll away. -->
    <div v-if="viewing" class="view-as-banner" role="status" data-test="view-as-banner">
      <IconEye :size="16" aria-hidden="true" />
      <span class="view-as-banner__text">
        {{ t('viewAs.banner', { name: user?.name ?? '', role: roleLabel }) }}
      </span>
      <button
        type="button"
        class="view-as-banner__exit"
        data-test="view-as-exit"
        :disabled="stopViewAs.isPending.value"
        @click="exitViewAs"
      >
        {{ t('viewAs.exit') }}
      </button>
    </div>

    <!-- The header's marker is not drawn on a phone, so the page carries it there. -->
    <DemonstrationMarker v-if="isDemonstration" class="shell__demo-banner" />

    <RailLayout>
      <template v-if="allRailItems.length > 0" #rail>
        <!-- On a phone the hamburger panel is the navigation; the scroller would repeat it. -->
        <SideNav
          class="shell__rail"
          collapsible
          :heading="railHeading"
          :items="activeRailItems"
          :foot-items="activeFootItems"
          :active="activeRail"
          @change="goTo"
        />
      </template>

      <RouterView />
      <AnalystDock
        v-if="askElfieOffered && workspace.data.value && analystContext"
        :workspace="workspace.data.value"
        :context="analystContext"
      />
    </RailLayout>

    <ViewAsModal
      v-if="isAdmin && user"
      :open="viewAsOpen"
      :pending="startViewAs.isPending.value"
      :self-id="user.id"
      @close="viewAsOpen = false"
      @start="beginViewAs"
    />

    <UserFormModal
      :open="profileOpen"
      mode="self"
      :initial="profileDraft"
      :pending="updateProfile.isPending.value"
      :avatar-url="user?.avatarUrl"
      :avatar-pending="setAvatar.isPending.value || removeAvatar.isPending.value"
      @avatar="(file: File) => changePicture(file)"
      @remove-avatar="changePicture(null)"
      @close="profileOpen = false"
      @submit="saveProfile"
    />
  </AuthorizedLayout>
</template>

<style scoped>
.shell__account {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-lg);
}

/* ─── Phone menu (inside the library's panel) ─── */
.phone-menu {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  padding: var(--spacing-md) var(--spacing-lg) var(--spacing-2xl);
  border-radius: var(--radius-4xl);
  background: var(--surface-panel);
}

.phone-menu__sections {
  display: flex;
  flex: 1;
  flex-direction: column;
}

.phone-menu__section {
  border-bottom: 1px solid var(--line-subtle);
}

.phone-menu__section-head {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-lg) var(--spacing-md);
  border: 0;
  background: transparent;
  color: var(--fg-primary);
  cursor: pointer;
  font-family: var(--font-family-primary);
  font-size: var(--font-size-body-1);
  line-height: var(--line-height-body-1);
  font-weight: var(--font-weight-semibold);
  text-align: left;
}

.phone-menu__caret {
  color: var(--fg-muted);
  transition: transform 160ms ease;
}

.phone-menu__section.is-open .phone-menu__caret {
  transform: rotate(180deg);
}

.phone-menu__list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xxs);
  margin: 0;
  padding: 0 0 var(--spacing-lg) var(--spacing-md);
  list-style: none;
}

.phone-menu__item {
  display: block;
  width: 100%;
  padding: var(--spacing-md) var(--spacing-lg);
  border: 0;
  border-radius: var(--radius-xl);
  background: transparent;
  color: var(--fg-primary);
  cursor: pointer;
  font-family: var(--font-family-primary);
  font-size: var(--font-size-body-2);
  line-height: var(--line-height-body-2);
  text-align: left;
}

.phone-menu__item--icon {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
}

.phone-menu__grow {
  flex: 1;
}

.phone-menu__value {
  color: var(--fg-muted);
  font-size: var(--font-size-body-3);
}

.phone-menu__item--danger {
  color: var(--fg-error-strong);
}

.phone-menu__item:hover,
.phone-menu__item:focus-visible {
  background: var(--surface-raised);
}

.phone-menu__item.is-active {
  background: var(--surface-raised);
  font-weight: var(--font-weight-semibold);
}

/* A link with its target is plain text on the panel, like the buttons beside it. */
a.phone-menu__item {
  text-decoration: none;
}

.phone-menu__item.is-disabled {
  color: var(--fg-muted);
  cursor: not-allowed;
}

.phone-menu__item.is-disabled:hover {
  background: transparent;
}

/* Why the item above does nothing, where a phone can read it. */
.phone-menu__note {
  margin: 0;
  padding: 0 var(--spacing-lg) var(--spacing-md) var(--spacing-3xl);
  color: var(--fg-muted);
  font-size: var(--font-size-body-3);
  line-height: var(--line-height-body-3);
}

.phone-menu__chips {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-sm);
  padding: var(--spacing-xs) var(--spacing-lg) var(--spacing-md) var(--spacing-3xl);
}

.phone-menu__chip {
  padding: var(--spacing-xs) var(--spacing-md);
  border: 1px solid var(--line-default);
  border-radius: var(--radius-full);
  background: transparent;
  color: var(--fg-primary);
  cursor: pointer;
  font-family: var(--font-family-primary);
  font-size: var(--font-size-body-3);
  line-height: var(--line-height-body-3);
}

.phone-menu__chip.is-active {
  border-color: var(--action-solid);
  background: var(--surface-raised);
  font-weight: var(--font-weight-semibold);
}

.phone-menu__foot {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  padding-top: var(--spacing-xl);
}

.phone-menu__build {
  align-self: flex-start;
  padding: var(--spacing-xs) var(--spacing-lg);
  border: 0;
  background: transparent;
  cursor: copy;
  font-family: var(--font-family-mono, var(--font-family-primary));
  font-size: var(--font-size-caption);
  line-height: var(--line-height-caption);
  color: var(--fg-muted);
}

/* The brand mark that stands in for the avatar on a phone: the coin alone. */
.shell__coin {
  display: none;
}

.shell__demo-chip {
  margin-inline-start: var(--spacing-lg);
}

@media (min-width: 1024px) {
  .shell__demo-banner {
    display: none;
  }
}

.shell__coin :deep(.lockup__word) {
  display: none;
}

/* Below 1024px the library's bar owns the layout: the panel is the navigation,
   the toggle lives in the panel, and the account area is display-only — so it
   shows the coin, not an avatar that cannot be tapped. */
@media (max-width: 1023px) {
  .shell__rail,
  .shell__theme,
  .shell__intelligence,
  .shell__account .account {
    display: none;
  }

  .shell__coin {
    display: inline-flex;
    align-items: center;
  }
}

/* A pill on the navy, the same figure/ground move the active nav item makes. */
.account {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-xs) var(--spacing-lg) var(--spacing-xs) var(--spacing-xs);
  border: 1px solid var(--line-on-field);
  border-radius: var(--radius-full);
  background: var(--surface-field-raised);
  cursor: pointer;
  font-family: var(--font-family-primary);
  text-align: left;
}

.account:hover {
  border-color: var(--fg-on-field-muted);
}

.account__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  flex-shrink: 0;
  border-radius: var(--radius-full);
  background: var(--surface-field-raised);
  border: 1px solid var(--line-on-field);
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-semibold);
  color: var(--fg-on-field);
}

.account__text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.account__name {
  font-size: var(--font-size-body-3);
  line-height: var(--line-height-body-3);
  font-weight: var(--font-weight-semibold);
  color: var(--fg-on-field);
  white-space: nowrap;
}

.account__role {
  font-size: var(--font-size-caption);
  line-height: var(--line-height-caption);
  color: var(--fg-on-field-muted);
  white-space: nowrap;
}

.account__chevron {
  flex-shrink: 0;
  color: var(--fg-on-field-muted);
}

.shell__brand {
  display: inline-flex;
  align-items: center;
  text-decoration: none;
}

/* The menu's account block, as a button. Mirrors the library's header layout. */
.profile-head {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  width: 100%;
  padding: var(--spacing-md) var(--spacing-lg);
  border: 0;
  border-radius: var(--radius-lg);
  background: transparent;
  cursor: pointer;
  font-family: var(--font-family-primary);
  text-align: left;
}

.profile-head:hover,
.profile-head:focus-visible {
  background: var(--surface-raised);
}

.profile-head__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  flex-shrink: 0;
  border-radius: var(--radius-full);
  background: var(--surface-raised);
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-semibold);
  color: var(--fg-muted);
}

.profile-head__text {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
}

.profile-head__name {
  font-size: var(--font-size-body-2);
  line-height: var(--line-height-body-2);
  font-weight: var(--font-weight-semibold);
  color: var(--fg-primary);
}

.profile-head__role {
  font-size: var(--font-size-body-3);
  line-height: var(--line-height-body-3);
  color: var(--fg-muted);
}

.profile-head__pencil {
  flex-shrink: 0;
  color: var(--fg-muted);
  opacity: 0;
  transition: opacity 120ms ease;
}

.profile-head:hover .profile-head__pencil,
.profile-head:focus-visible .profile-head__pencil {
  opacity: 1;
}

/* Loud on purpose: an admin must never forget whose eyes they are using. */
.view-as-banner {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-lg);
  padding: var(--spacing-sm) var(--spacing-lg);
  border: 1px solid var(--action-solid);
  border-radius: var(--radius-lg);
  background: var(--surface-raised);
  color: var(--fg-primary);
  font-size: var(--font-size-body-3);
  line-height: var(--line-height-body-3);
}

.view-as-banner__text {
  flex: 1;
  min-width: 0;
}

.view-as-banner__exit {
  padding: var(--spacing-xs) var(--spacing-md);
  border: 1px solid var(--line-default);
  border-radius: var(--radius-full);
  background: var(--surface-panel);
  color: var(--fg-primary);
  cursor: pointer;
  font: inherit;
  font-weight: var(--font-weight-semibold);
}

.view-as-banner__exit:hover,
.view-as-banner__exit:focus-visible {
  border-color: var(--action-solid);
}

/* Deliberately quiet: caption size, muted, no border — a footnote, not a row. */
.build-stamp {
  width: 100%;
  padding: var(--spacing-xs) var(--spacing-md);
  border: 0;
  background: transparent;
  cursor: copy;
  font-family: var(--font-family-mono, var(--font-family-primary));
  font-size: var(--font-size-caption);
  line-height: var(--line-height-caption);
  color: var(--fg-muted);
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.build-stamp:hover,
.build-stamp:focus-visible {
  color: var(--fg-primary);
}
</style>
